'use client';
import React, { useState, useEffect } from 'react';
import { FeaturedProducts as SharedFeaturedProducts } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import TemplateProductCard from '@/modules/products/components/product-preview/template-product-card';
import type { FeaturedProductsProps, SharedProduct } from '@launchstore/shared-puck';

// Placeholder for storefront's ProductPreview integration. The real
// ProductPreview lives in src/modules/products/components/product-preview —
// it's a complex storefront-only component (~5 variants) that requires
// StoreRegion data. This thin wrapper:
//   1. fetches the storefront API for products
//   2. fetches the storefront region
//   3. passes both as props to the shared presentational component
//   4. passes renderProduct={(product) => <ProductPreview product region isFeatured={false} />}
//      via D-1 escape hatch — shared stays simple, storefront keeps its rich card.
//
// NOTE: the actual <ProductPreview> import + display logic is filled in
// when Wave 2.2 migrates ProductPreview itself. Until then the storefront
// wrapper falls back to a generic card (matching the shared default).
const SharedFeaturedProductsRender = (SharedFeaturedProducts as any).render;

const FeaturedProductsImpl: React.FC<FeaturedProductsProps> = (props: any) => {
  const { context } = usePuckContext() as any;
  const cardTemplate = context?.productCardTemplate;
  const [rawById, setRawById] = useState<Record<string, any>>({});
  const [products, setProducts] = useState<SharedProduct[]>([]);
  const [region, setRegion] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let active = true;
    async function fetchAll() {
      setLoading(true); setError('');
      try {
        const params = new URLSearchParams({ limit: String(props.maxProducts || 12) });
        switch (props.productSource) {
          case 'new': params.set('order', '-created_at'); break;
          case 'category': if (props.categoryId) params.set('category_id', props.categoryId); break;
          case 'manual': if (props.productIds) params.set('id', props.productIds); break;
        }
        // Page country → correct region for pricing; /api/products falls
        // back to the store's first region when it doesn't match
        const pathCountry = (typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '') || 'us';
        params.set('countryCode', (context as any)?.countryCode || pathCountry);
        const res = await fetch(`/api/products?${params}`);
        const data = await res.json();
        const raw: any[] = data.products || [];
        if (!active) return;
        const projected: SharedProduct[] = raw.map((p: any) => ({
          id: p.id, title: p.title, handle: p.handle,
          thumbnail: p.thumbnail || p.images?.[0]?.url,
          price: p.variants?.[0]?.calculated_price?.calculated_amount ?? null,
        }));
        setProducts(projected);
        setRawById(Object.fromEntries(raw.map((p: any) => [p.id, p])));

        // Region (best-effort; some storefronts don't need it for the default card)
        try {
          const rres = await fetch('/api/region');
          const rdata = await rres.json();
          if (active) setRegion(rdata.region || null);
        } catch { /* noop */ }
      } catch (err: any) {
        if (active) setError(err?.message || 'Failed to fetch products');
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchAll();
    return () => { active = false; };
  }, [props.productSource, props.categoryId, props.productIds, props.maxProducts]);

  // TODO(Wave 2.2): import ProductPreview from
  // '@modules/products/components/product-preview' and pass as
  // renderProduct={(p) => <ProductPreview product={apiProduct} region={region} isFeatured={false} />}
  // For now the shared defaultCard is used.

  return (
    <SharedFeaturedProductsRender
      {...props}
      products={products}
      loading={loading}
      error={error}
      // region available here for future D-1 wiring when ProductPreview is imported
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderProduct={
        cardTemplate && region
          ? (p: any) => {
              const raw = rawById[p.id];
              if (!raw) return null;
              return (
                <TemplateProductCard
                  product={raw}
                  region={region}
                  productCardTemplate={cardTemplate}
                  countryCode={context?.countryCode || 'us'}
                />
              );
            }
          : undefined
      }
    />
  );
};

export const FeaturedProducts: any = { ...SharedFeaturedProducts, render: (props: any) => <FeaturedProductsImpl {...props} /> };
export default FeaturedProducts;
